/** XeAgents.ai**/
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { AlertCircle, CheckCircle2, Globe, Loader2, Package } from "lucide-react";
import { Field } from "./StepCard";
import type { DetectedProduct, ExtractionStage } from "./types";

/**
 * The URL field, and everything the read of it has to say back.
 *
 * A URL is the one thing every customer has to hand, so it is the page's
 * shortcut past the part nobody completes: writing your own positioning into a
 * textarea. The button that acts on it lives at the foot of Step 2 rather than
 * here, because one press now reads the site AND writes the whole of Step 3
 * from it together with the rest of these answers.
 */
export function WebsiteExtractor({
  url,
  onUrlChange,
  stage,
  steps,
  onChooseProduct,
}: {
  url: string;
  onUrlChange: (value: string) => void;
  stage: ExtractionStage;
  steps: readonly string[];
  onChooseProduct: (product: DetectedProduct) => void;
}) {
  const busy = stage.status === 'analyzing';
  const done = stage.status === 'done';

  return (
    <div className="space-y-3">
      <Field
        label="Website URL"
        htmlFor="website-url"
        hint={
          <>
            Use a specific product page for a sharper result — <span className="font-mono text-[11px]">/products/voice-ai</span>{" "}
            writes about that product only.
          </>
        }
      >
        <div className="flex flex-col gap-2 sm:flex-row">
          <div className="relative flex-1">
            <Globe className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              id="website-url"
              type="url"
              inputMode="url"
              value={url}
              onChange={(event) => onUrlChange(event.target.value)}
              placeholder="https://yourcompany.com/products/voice-ai"
              className="pl-9"
              disabled={busy}
              data-testid="input-website-url"
            />
          </div>
        </div>
      </Field>

      {busy && <AnalysisChecklist steps={steps} current={stage.step} />}
      {stage.status === 'choosing_product' && (
        <ProductPicker products={stage.products} siteName={stage.siteName} onChoose={onChooseProduct} />
      )}
      {done && <ExtractionSuccess stage={stage} />}
      {stage.status === 'failed' && (
        <div className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/5 p-3">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
          <p className="text-xs text-destructive" data-testid="text-extraction-error">
            {stage.message} You can still fill everything in by hand below.
          </p>
        </div>
      )}
    </div>
  );
}

/**
 * The five things the analysis says it is doing.
 *
 * Reading a site takes long enough that a bare spinner reads as a hang. Naming
 * the steps also makes the result legible afterwards: a user who watched
 * "Identifying common objections" go by understands where the objections in
 * Step 3 came from, and reviews them instead of trusting them.
 */
function AnalysisChecklist({ steps, current }: { steps: readonly string[]; current: number }) {
  return (
    <div className="rounded-lg border bg-muted/30 p-4" data-testid="extraction-progress">
      <p className="mb-3 flex items-center gap-2 text-sm font-medium">
        <Loader2 className="h-4 w-4 animate-spin text-primary" />
        Analyzing website...
      </p>
      <ul className="space-y-1.5">
        {steps.map((step, index) => {
          const complete = index < current;
          const active = index === current;
          return (
            <li
              key={step}
              className={`flex items-center gap-2 text-xs ${
                complete ? "text-foreground" : active ? "text-foreground" : "text-muted-foreground/60"
              }`}
            >
              {complete ? (
                <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-emerald-600 dark:text-emerald-400" />
              ) : active ? (
                <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-primary" />
              ) : (
                <span className="h-3.5 w-3.5 shrink-0 rounded-full border border-current opacity-40" />
              )}
              {step}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/**
 * Which product this template is for.
 *
 * A homepage advertising Voice AI, a CRM and a recruitment platform cannot
 * become one pitch. Blending them produces a script describing a company that
 * sells everything, which a listener hears as a mailing list - so the page stops
 * and asks rather than guessing.
 */
function ProductPicker({
  products,
  siteName,
  onChoose,
}: {
  products: DetectedProduct[];
  siteName: string;
  onChoose: (product: DetectedProduct) => void;
}) {
  return (
    <div className="rounded-lg border border-primary/25 bg-primary/5 p-4" data-testid="product-picker">
      <p className="text-sm font-medium">We found multiple products{siteName ? ` on ${siteName}` : ""}</p>
      <p className="mt-0.5 text-xs text-muted-foreground">
        Select the product you want to create this template for. We'll read only that product's page.
      </p>
      <div className="mt-3 flex flex-wrap gap-2">
        {products.map((product) => (
          <Button
            key={product.url}
            type="button"
            variant="outline"
            size="sm"
            className="bg-background"
            onClick={() => onChoose(product)}
            data-testid={`button-product-${product.url}`}
          >
            <Package className="mr-2 h-3.5 w-3.5 text-primary" />
            {product.name}
          </Button>
        ))}
      </div>
    </div>
  );
}

const EXTRACTED = [
  "Company information",
  "Product details",
  "Unique value proposition",
  "Key features",
  "Common objections",
];

/** What the read produced, and at what scope - the scope is the reassurance. */
function ExtractionSuccess({ stage }: { stage: Extract<ExtractionStage, { status: 'done' }> }) {
  return (
    <div
      className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-4 dark:border-emerald-900/50 dark:bg-emerald-950/20"
      data-testid="extraction-success"
    >
      <div className="flex flex-wrap items-center gap-2">
        <CheckCircle2 className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
        <p className="text-sm font-medium text-emerald-900 dark:text-emerald-200">Website analyzed successfully</p>
        <Badge variant="outline" className="h-5 border-emerald-300 px-1.5 text-[10px] font-normal text-emerald-700 dark:text-emerald-300">
          {stage.scope === 'product'
            ? `Product scope${stage.productName ? `: ${stage.productName}` : ''}`
            : `Company scope · ${stage.pagesRead.length} page${stage.pagesRead.length === 1 ? '' : 's'}`}
        </Badge>
      </div>
      <ul className="mt-3 grid gap-1 sm:grid-cols-2">
        {EXTRACTED.map((item) => (
          <li key={item} className="flex items-center gap-1.5 text-xs text-emerald-900/80 dark:text-emerald-200/80">
            <CheckCircle2 className="h-3 w-3 shrink-0" />
            {item}
          </li>
        ))}
      </ul>
      <p className="mt-3 text-xs text-emerald-900/70 dark:text-emerald-200/70">
        Everything below is editable — review it before saving.
      </p>
    </div>
  );
}
